import {
  type ClipboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactElement,
} from 'react';
import { COMMENT_BODY_MAX_LENGTH, parseMentions, type BoardMember } from '@molt/shared';
import {
  Bold,
  Code,
  CreditCard,
  Italic,
  LayoutDashboard,
  List,
  ListOrdered,
  Minus,
  Paperclip,
  Quote,
  Strikethrough,
  X,
} from 'lucide-react';

import {
  Button,
  Combobox,
  Dialog,
  EmptyState,
  IconButton,
  Notice,
  TextField,
  profileName,
} from '@/components';
import { announce } from '@/lib/announce';
import {
  groupMentionConfirmation,
  type GroupMentionOption,
  type MentionCandidate,
} from '@/lib/comments';
import { useCommentTypingPublisher } from '@/lib/comment-typing';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  applyRichTextPaste,
  formatMarkdown,
  markdownFormatFallbacks,
} from '@/lib/markdown-editing';

export interface PendingCommentAttachment {
  readonly id: string;
  readonly kind: 'file' | 'card_link' | 'board_link';
  readonly file?: File | undefined;
  readonly displayName: string;
  readonly targetCardId?: string | undefined;
  readonly targetBoardId?: string | undefined;
}

export interface CommentComposerSubmit {
  readonly body: string;
  readonly attachments: readonly PendingCommentAttachment[];
}

export interface CommentCardLinkOption {
  readonly id: string;
  readonly title: string;
  readonly boardTitle?: string | undefined;
  readonly listTitle?: string | undefined;
}

export interface CommentBoardLinkOption {
  readonly id: string;
  readonly title: string;
}

export interface CommentComposerProps {
  readonly busy?: boolean;
  readonly attachmentBusy?: boolean;
  readonly draftSeed?: { readonly id: string; readonly body: string } | undefined;
  readonly mentionMembers?: readonly BoardMember[];
  readonly mentionCandidates?: readonly MentionCandidate[];
  readonly groupMentions?: readonly GroupMentionOption[];
  readonly cardLinkOptions?: readonly CommentCardLinkOption[];
  readonly boardLinkOptions?: readonly CommentBoardLinkOption[];
  readonly addMentionCandidateBusyId?: string | undefined;
  readonly onAddMentionCandidate?:
    ((candidate: MentionCandidate) => Promise<void> | void) | undefined;
  readonly onTypingStateChange?: ((typing: boolean) => void) | undefined;
  readonly onSubmit: (input: CommentComposerSubmit) => void;
}

const COMMENT_BODY_WARN_AT = 16_000;

interface MentionComboboxOption {
  readonly id: string;
  readonly username: string;
  readonly label: string;
  readonly description?: string | undefined;
  readonly rank?: MentionCandidate['rank'] | undefined;
}

export function CommentComposer({
  busy = false,
  attachmentBusy = false,
  draftSeed,
  mentionMembers = [],
  mentionCandidates,
  groupMentions = [],
  cardLinkOptions = [],
  boardLinkOptions = [],
  addMentionCandidateBusyId,
  onAddMentionCandidate,
  onTypingStateChange,
  onSubmit,
}: CommentComposerProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const markdownFallbacks = useMemo(() => markdownFormatFallbacks(t), [t]);
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingCommentAttachment[]
  >([]);
  const [cardLinkQuery, setCardLinkQuery] = useState('');
  const [boardLinkQuery, setBoardLinkQuery] = useState('');
  const [pendingMentionCandidate, setPendingMentionCandidate] =
    useState<MentionCandidate | null>(null);
  const [locallyAddedMentionUsernames, setLocallyAddedMentionUsernames] = useState<
    readonly string[]
  >([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { noteDraftValue, stopTyping } = useCommentTypingPublisher((state) =>
    onTypingStateChange?.(state === 'started'),
  );
  const [confirmation, setConfirmation] = useState<GroupMentionOption | null>(null);
  const mentionQuery = activeMentionQuery(body);
  const resolvedMentionCandidates = useMemo(
    () =>
      mentionCandidates ??
      mentionMembers.map((member) => ({
        id: member.id,
        username: member.username,
        label: profileName(member),
        description: t('cardBack.comments.mention.boardMember'),
        rank: 'board' as const,
      })),
    [mentionCandidates, mentionMembers, t],
  );

  useEffect(() => {
    if (draftSeed === undefined) return;
    setBody(draftSeed.body);
    setError(null);
    noteDraftValue(draftSeed.body);
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      textarea?.focus();
      textarea?.setSelectionRange(draftSeed.body.length, draftSeed.body.length);
    });
  }, [draftSeed, noteDraftValue]);
  const groupMentionOptions = useMemo<MentionComboboxOption[]>(
    () =>
      mentionQuery === null
        ? []
        : groupMentions
            .filter((option) =>
              option.username
                .toLocaleLowerCase()
                .includes(mentionQuery.toLocaleLowerCase()),
            )
            .map(
              (option) =>
                ({
                  id: option.id,
                  label: option.label,
                  username: option.username,
                  description: option.description,
                }) satisfies MentionComboboxOption,
            ),
    [groupMentions, mentionQuery],
  );
  const mentionOptions = useMemo<MentionComboboxOption[]>(
    () =>
      mentionQuery === null
        ? []
        : resolvedMentionCandidates
            .filter((candidate) => {
              const haystack =
                `${candidate.username} ${candidate.label} ${candidate.description}`.toLocaleLowerCase();
              return haystack.includes(mentionQuery.toLocaleLowerCase());
            })
            .slice(0, 8)
            .map((candidate) => candidate satisfies MentionComboboxOption),
    [mentionQuery, resolvedMentionCandidates],
  );
  const mentionOptionGroups = useMemo(
    () =>
      [
        {
          id: 'card-members',
          label: t('cardBack.comments.composer.mention.cardMembersHeading'),
          options: mentionOptions.filter((option) => option.rank === 'card'),
        },
        {
          id: 'board-members',
          label: t('cardBack.comments.composer.mention.boardMembersHeading'),
          options: mentionOptions.filter((option) => option.rank === 'board'),
        },
        {
          id: 'workspace-members',
          label: t('cardBack.comments.composer.mention.workspaceMembersHeading'),
          options: mentionOptions.filter((option) => option.rank === 'workspace'),
        },
      ].filter((group) => group.options.length > 0),
    [mentionOptions, t],
  );
  const blockedMentionCandidate = useMemo(
    () =>
      pendingMentionCandidate ??
      resolvedMentionCandidates.find((candidate) => {
        if (candidate.rank !== 'workspace') return false;
        if (locallyAddedMentionUsernames.includes(candidate.username.toLowerCase())) {
          return false;
        }
        return bodyMentionsUsername(body, candidate.username);
      }) ??
      null,
    [
      body,
      locallyAddedMentionUsernames,
      pendingMentionCandidate,
      resolvedMentionCandidates,
    ],
  );
  const cardLinkOptionsById = useMemo(
    () => new Map(cardLinkOptions.map((option) => [option.id, option])),
    [cardLinkOptions],
  );
  const boardLinkOptionsById = useMemo(
    () => new Map(boardLinkOptions.map((option) => [option.id, option])),
    [boardLinkOptions],
  );
  const linkedCardIds = useMemo(
    () =>
      new Set(
        pendingAttachments
          .filter((attachment) => attachment.kind === 'card_link')
          .map((attachment) => attachment.targetCardId),
      ),
    [pendingAttachments],
  );
  const filteredCardLinkOptions = useMemo(
    () =>
      cardLinkOptions
        .filter((option) => !linkedCardIds.has(option.id))
        .filter((option) => {
          const query = cardLinkQuery.trim().toLocaleLowerCase();
          if (query.length === 0) return true;
          const haystack = `${option.title} ${option.boardTitle ?? ''} ${
            option.listTitle ?? ''
          }`.toLocaleLowerCase();
          return haystack.includes(query);
        })
        .slice(0, 8)
        .map((option) => ({
          id: option.id,
          label: option.title,
          title: option.title,
          boardTitle: option.boardTitle,
          listTitle: option.listTitle,
        })),
    [cardLinkOptions, cardLinkQuery, linkedCardIds],
  );
  const linkedBoardIds = useMemo(
    () =>
      new Set(
        pendingAttachments
          .filter((attachment) => attachment.kind === 'board_link')
          .map((attachment) => attachment.targetBoardId),
      ),
    [pendingAttachments],
  );
  const filteredBoardLinkOptions = useMemo(
    () =>
      boardLinkOptions
        .filter((option) => !linkedBoardIds.has(option.id))
        .filter((option) => {
          const query = boardLinkQuery.trim().toLocaleLowerCase();
          if (query.length === 0) return true;
          return option.title.toLocaleLowerCase().includes(query);
        })
        .slice(0, 8)
        .map((option) => ({
          id: option.id,
          label: option.title,
          title: option.title,
        })),
    [boardLinkOptions, boardLinkQuery, linkedBoardIds],
  );

  function finishSubmit(trimmed: string) {
    setError(null);
    stopTyping();
    onSubmit({ body: trimmed, attachments: pendingAttachments });
    setBody('');
    setPendingAttachments([]);
    setCardLinkQuery('');
    setBoardLinkQuery('');
    setConfirmation(null);
    setPendingMentionCandidate(null);
    setLocallyAddedMentionUsernames([]);
    if (fileInputRef.current !== null) fileInputRef.current.value = '';
  }

  function submit() {
    const trimmed = body.trim();
    if (trimmed.length === 0) {
      setError(t('cardBack.comments.error.empty'));
      return;
    }
    if (trimmed.length > COMMENT_BODY_MAX_LENGTH) {
      setError(t('cardBack.comments.error.tooLong'));
      return;
    }
    if (blockedMentionCandidate !== null) {
      setPendingMentionCandidate(blockedMentionCandidate);
      setError(null);
      return;
    }
    const confirm = groupMentionConfirmation(trimmed, groupMentions);
    if (confirm !== null) {
      setError(null);
      setConfirmation(confirm);
      return;
    }
    finishSubmit(trimmed);
  }

  function applyFormatting(
    mode:
      'bold' | 'italic' | 'strike' | 'code' | 'bullet' | 'number' | 'quote' | 'divider',
  ) {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? body.length;
    const end = textarea?.selectionEnd ?? body.length;
    const selected = body.slice(start, end);
    const next = formatMarkdown(markdownFallbacks, body, start, end, selected, mode);
    setBody(next.value);
    noteDraftValue(next.value);
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(next.selectionStart, next.selectionEnd);
    });
  }

  function handlePaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const html = event.clipboardData.getData('text/html');
    const textarea = textareaRef.current;
    const result = applyRichTextPaste({
      value: body,
      html,
      selectionStart: textarea?.selectionStart ?? body.length,
      selectionEnd: textarea?.selectionEnd ?? body.length,
      variant: 'comment',
    });

    if (!result.handled) return;
    event.preventDefault();
    setBody(result.value);
    noteDraftValue(result.value);
    if (error !== null) setError(null);
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(result.selectionStart, result.selectionEnd);
    });
  }

  function addFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.currentTarget.files ?? []);
    if (files.length === 0) return;
    const attachments = files.map((file) => ({
      id: createDraftAttachmentId('file'),
      kind: 'file' as const,
      file,
      displayName: file.name,
    }));
    setPendingAttachments((current) => [...current, ...attachments]);
    const firstAttachment = attachments[0];
    announce(
      attachments.length === 1 && firstAttachment !== undefined
        ? t('cardBack.comments.announce.attachmentAdded', {
            name: firstAttachment.displayName,
          })
        : t(
            'cardBack.comments.announce.attachmentsAdded',
            { count: attachments.length },
            attachments.length,
          ),
    );
    event.currentTarget.value = '';
  }

  function addCardLink(optionId: string) {
    const option = cardLinkOptionsById.get(optionId);
    if (option === undefined) return;
    setPendingAttachments((current) => [
      ...current,
      {
        id: createDraftAttachmentId('card'),
        kind: 'card_link',
        displayName: option.title,
        targetCardId: option.id,
      },
    ]);
    setCardLinkQuery('');
    announce(t('cardBack.comments.announce.attachmentAdded', { name: option.title }));
  }

  function addBoardLink(optionId: string) {
    const option = boardLinkOptionsById.get(optionId);
    if (option === undefined) return;
    setPendingAttachments((current) => [
      ...current,
      {
        id: createDraftAttachmentId('board'),
        kind: 'board_link',
        displayName: option.title,
        targetBoardId: option.id,
      },
    ]);
    setBoardLinkQuery('');
    announce(t('cardBack.comments.announce.attachmentAdded', { name: option.title }));
  }

  function removeAttachment(attachmentId: string) {
    setPendingAttachments((current) => {
      const removed = current.find((attachment) => attachment.id === attachmentId);
      if (removed !== undefined) {
        announce(
          t('cardBack.comments.announce.attachmentRemoved', {
            name: removed.displayName,
          }),
        );
      }
      return current.filter((attachment) => attachment.id !== attachmentId);
    });
  }

  async function addPendingMentionCandidate(candidate: MentionCandidate): Promise<void> {
    if (onAddMentionCandidate === undefined) return;
    await onAddMentionCandidate(candidate);
    setLocallyAddedMentionUsernames((current) => [
      ...current,
      candidate.username.toLowerCase(),
    ]);
    setPendingMentionCandidate(null);
    announce(
      t('cardBack.comments.announce.mentionCandidateAdded', { name: candidate.label }),
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <TextField
        ref={textareaRef}
        label={t('cardBack.comments.composer.bodyLabel')}
        multiline
        value={body}
        onChange={(event) => {
          const next = event.currentTarget.value;
          setBody(next);
          noteDraftValue(next);
          if (error !== null) setError(null);
        }}
        onBlur={stopTyping}
        onPaste={handlePaste}
        {...(error === null ? {} : { error })}
        counter={{
          value: body.trim().length,
          max: COMMENT_BODY_MAX_LENGTH,
          warnAt: COMMENT_BODY_WARN_AT,
        }}
      />
      <div
        className="flex flex-wrap gap-1"
        aria-label={t('cardBack.comments.composer.formatting.toolbar')}
      >
        <IconButton
          label={t('cardBack.comments.composer.formatting.bold')}
          size="sm"
          onClick={() => applyFormatting('bold')}
        >
          <Bold aria-hidden="true" className="size-4" />
        </IconButton>
        <IconButton
          label={t('cardBack.comments.composer.formatting.italic')}
          size="sm"
          onClick={() => applyFormatting('italic')}
        >
          <Italic aria-hidden="true" className="size-4" />
        </IconButton>
        <IconButton
          label={t('cardBack.comments.composer.formatting.strikethrough')}
          size="sm"
          onClick={() => applyFormatting('strike')}
        >
          <Strikethrough aria-hidden="true" className="size-4" />
        </IconButton>
        <IconButton
          label={t('cardBack.comments.composer.formatting.inlineCode')}
          size="sm"
          onClick={() => applyFormatting('code')}
        >
          <Code aria-hidden="true" className="size-4" />
        </IconButton>
        <IconButton
          label={t('cardBack.comments.composer.formatting.bulletedList')}
          size="sm"
          onClick={() => applyFormatting('bullet')}
        >
          <List aria-hidden="true" className="size-4" />
        </IconButton>
        <IconButton
          label={t('cardBack.comments.composer.formatting.numberedList')}
          size="sm"
          onClick={() => applyFormatting('number')}
        >
          <ListOrdered aria-hidden="true" className="size-4" />
        </IconButton>
        <IconButton
          label={t('cardBack.comments.composer.formatting.quote')}
          size="sm"
          onClick={() => applyFormatting('quote')}
        >
          <Quote aria-hidden="true" className="size-4" />
        </IconButton>
        <IconButton
          label={t('cardBack.comments.composer.formatting.divider')}
          size="sm"
          onClick={() => applyFormatting('divider')}
        >
          <Minus aria-hidden="true" className="size-4" />
        </IconButton>
      </div>
      {mentionQuery !== null ? (
        <Combobox
          label={t('cardBack.comments.composer.mention.comboboxLabel')}
          hideLabel
          autoFocus
          value={mentionQuery}
          onChange={(value) => {
            const next = replaceActiveMention(body, value, false);
            setBody(next);
            noteDraftValue(next);
          }}
          minLength={2}
          groups={[
            {
              id: 'groups',
              label: t('cardBack.comments.composer.mention.groupsHeading'),
              options: groupMentionOptions,
            },
            ...mentionOptionGroups,
          ]}
          emptyState={
            mentionQuery.length < 2 ? (
              <EmptyState
                tone="neutral"
                size="inline"
                title={t('cardBack.comments.composer.mention.typeTwoCharacters')}
              />
            ) : (
              <EmptyState
                tone="neutral"
                size="inline"
                title={t('cardBack.comments.composer.mention.noMatches')}
              />
            )
          }
          onActivate={(option) => {
            const next = replaceActiveMention(body, option.username, true);
            setBody(next);
            noteDraftValue(next);
            setPendingMentionCandidate(
              option.rank === 'workspace'
                ? (resolvedMentionCandidates.find(
                    (candidate) => candidate.id === option.id,
                  ) ?? null)
                : null,
            );
            // The combobox held focus and unmounts with the committed mention,
            // so hand focus back to the textarea, caret after the inserted name.
            window.setTimeout(() => {
              const textarea = textareaRef.current;
              textarea?.focus();
              textarea?.setSelectionRange(next.length, next.length);
            }, 0);
          }}
          renderOption={(option) => (
            <span className="flex min-w-0 flex-col">
              <span className="truncate">{option.label}</span>
              <span className="truncate text-xs text-ink-muted">
                {mentionOptionDescription(option)}
              </span>
            </span>
          )}
        />
      ) : null}
      {blockedMentionCandidate === null ? null : (
        <Notice
          tone="warn"
          title={t('cardBack.comments.composer.mention.notOnBoardTitle', {
            name: blockedMentionCandidate.label,
          })}
          body={t('cardBack.comments.composer.mention.notOnBoardBody')}
          action={
            <Button
              size="sm"
              variant="primary"
              busy={addMentionCandidateBusyId === blockedMentionCandidate.id}
              busyLabel={t('cardBack.comments.composer.mention.addingMember')}
              disabled={onAddMentionCandidate === undefined}
              onClick={() => {
                void addPendingMentionCandidate(blockedMentionCandidate);
              }}
            >
              {t('cardBack.comments.composer.mention.addToBoard')}
            </Button>
          }
        />
      )}
      <div>
        {/* Every control here is 36px tall, the link comboboxes' height, so the
            row lines up on centre instead of leaving a short button on the floor. */}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" busy={busy} onClick={submit}>
            {t('cardBack.comments.composer.submit')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            tabIndex={-1}
            aria-hidden="true"
            className="hidden"
            data-testid="comment-file-input"
            onChange={addFiles}
          />
          <IconButton
            label={t('cardBack.comments.composer.attach')}
            busy={attachmentBusy}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip aria-hidden="true" className="size-4" />
          </IconButton>
          {cardLinkOptions.length > 0 ? (
            <div className="min-w-56 max-w-full flex-1 sm:max-w-80">
              <Combobox
                label={t('cardBack.comments.composer.cardLink.label')}
                hideLabel
                value={cardLinkQuery}
                onChange={setCardLinkQuery}
                openWhenEmpty
                placeholder={t('cardBack.comments.composer.cardLink.placeholder')}
                groups={[
                  {
                    id: 'cards',
                    label: t('cardBack.comments.composer.cardLink.group'),
                    options: filteredCardLinkOptions,
                  },
                ]}
                emptyState={
                  <EmptyState
                    tone="neutral"
                    size="inline"
                    title={t('cardBack.comments.composer.cardLink.noMatches')}
                  />
                }
                onActivate={(option) => addCardLink(option.id)}
                renderOption={(option) => (
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{option.title}</span>
                    {option.boardTitle === undefined &&
                    option.listTitle === undefined ? null : (
                      <span className="truncate text-xs text-ink-muted">
                        {[option.boardTitle, option.listTitle]
                          .filter(Boolean)
                          .join(' / ')}
                      </span>
                    )}
                  </span>
                )}
              />
            </div>
          ) : null}
          {boardLinkOptions.length > 0 ? (
            <div className="min-w-56 max-w-full flex-1 sm:max-w-80">
              <Combobox
                label={t('cardBack.comments.composer.boardLink.label')}
                hideLabel
                value={boardLinkQuery}
                onChange={setBoardLinkQuery}
                openWhenEmpty
                placeholder={t('cardBack.comments.composer.boardLink.placeholder')}
                groups={[
                  {
                    id: 'boards',
                    label: t('cardBack.comments.composer.boardLink.group'),
                    options: filteredBoardLinkOptions,
                  },
                ]}
                emptyState={
                  <EmptyState
                    tone="neutral"
                    size="inline"
                    title={t('cardBack.comments.composer.boardLink.noMatches')}
                  />
                }
                onActivate={(option) => addBoardLink(option.id)}
                renderOption={(option) => (
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{option.title}</span>
                    <span className="truncate text-xs text-ink-muted">
                      {t('cardBack.comments.composer.boardLink.optionKind')}
                    </span>
                  </span>
                )}
              />
            </div>
          ) : null}
        </div>
      </div>
      {pendingAttachments.length > 0 ? (
        <ul
          className="flex flex-col gap-1"
          aria-label={t('cardBack.comments.composer.pendingAttachments')}
        >
          {pendingAttachments.map((attachment) => (
            <li
              key={attachment.id}
              className="flex min-w-0 items-center gap-2 rounded-card border border-border bg-surface-raised px-2 py-1.5 text-sm text-ink"
            >
              {attachment.kind === 'file' ? (
                <Paperclip
                  aria-hidden="true"
                  className="size-4 shrink-0 text-ink-muted"
                />
              ) : attachment.kind === 'card_link' ? (
                <CreditCard
                  aria-hidden="true"
                  className="size-4 shrink-0 text-ink-muted"
                />
              ) : (
                <LayoutDashboard
                  aria-hidden="true"
                  className="size-4 shrink-0 text-ink-muted"
                />
              )}
              <span className="min-w-0 flex-1 truncate">{attachment.displayName}</span>
              <IconButton
                label={t('cardBack.comments.composer.removeAttachment', {
                  name: attachment.displayName,
                })}
                size="sm"
                onClick={() => removeAttachment(attachment.id)}
              >
                <X aria-hidden="true" className="size-4" />
              </IconButton>
            </li>
          ))}
        </ul>
      ) : null}
      <Dialog
        open={confirmation !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmation(null);
        }}
        size="sm"
        title={t('cardBack.comments.composer.groupMention.confirmTitle')}
        description={
          confirmation === null
            ? undefined
            : t(
                'cardBack.comments.composer.groupMention.confirmBody',
                { count: confirmation.notifyCount, group: confirmation.username },
                confirmation.notifyCount,
              )
        }
        closeControl={
          <IconButton
            label={t('cardBack.comments.composer.groupMention.close')}
            size="sm"
          >
            <X aria-hidden="true" className="size-4" />
          </IconButton>
        }
        footer={
          <>
            <Button size="sm" variant="quiet" onClick={() => setConfirmation(null)}>
              {t('cardBack.comments.composer.groupMention.cancel')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              busy={busy}
              onClick={() => finishSubmit(body.trim())}
            >
              {t('cardBack.comments.composer.groupMention.post')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-muted">
          {t('cardBack.comments.composer.groupMention.guidance')}
        </p>
      </Dialog>
    </div>
  );
}

function activeMentionQuery(body: string): string | null {
  const match = /(^|[ \t\n\r\f\v([{"'`<])@([A-Za-z0-9._-]*)$/.exec(body);
  if (match === null) return null;
  const start = match.index + (match[1]?.length ?? 0);
  if (isInsideMarkdownCode(body, start)) return null;
  return match[2] ?? null;
}

function replaceActiveMention(body: string, value: string, commit: boolean): string {
  return body.replace(
    /(^|[ \t\n\r\f\v([{"'`<])@([A-Za-z0-9._-]*)$/,
    (_match, prefix: string) => {
      const suffix = commit ? ' ' : '';
      return `${prefix}@${value}${suffix}`;
    },
  );
}

function mentionOptionDescription(option: MentionComboboxOption): string {
  return option.description ?? `@${option.username}`;
}

function bodyMentionsUsername(body: string, username: string): boolean {
  return new Set(parseMentions(body)).has(username.toLowerCase());
}

function isInsideMarkdownCode(body: string, index: number): boolean {
  const before = body.slice(0, index);
  const lines = before.split('\n');
  const previousLines = lines.slice(0, -1);
  return previousLines.reduce(
    (inside, line) => (/^\s*```/.test(line) ? !inside : inside),
    false,
  );
}

function createDraftAttachmentId(prefix: string): string {
  const random =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}:${random}`;
}
